import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../../services/api';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface SalesChartProps {
  period: string;
}

type TimelinePeriod = 'last_30_days' | 'last_12_weeks' | 'last_12_months';

const periodMap: Record<string, TimelinePeriod> = {
  'Senaste 30 dagarna': 'last_30_days',
  'Senaste 3 månaderna': 'last_12_weeks',
  'Senaste året': 'last_12_months'
};

const SalesChart: React.FC<SalesChartProps> = ({ period }) => {
  const [chartData, setChartData] = useState<{labels: string[]; orders: number[]; revenue: number[]} | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchSalesData = useCallback(async () => {
    try {
      setLoading(true);
      const timelineKey = periodMap[period] ?? 'last_30_days';
      const timeline = await apiService.getOrderSalesTimeline(timelineKey);

      if (!timeline || !timeline.labels || timeline.labels.length === 0) {
        setChartData({ labels: ['Ingen data'], orders: [0], revenue: [0] });
        return;
      }

      setChartData({
        labels: timeline.labels,
        orders: timeline.orders,
        revenue: timeline.revenue
      });
    } catch (error) {
      console.error('Error fetching sales data:', error);
      setChartData({ labels: ['Ingen data'], orders: [0], revenue: [0] });
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchSalesData();
  }, [fetchSalesData]);

  if (loading || !chartData) {
    return <div style={{ textAlign: 'center', padding: '2rem' }}>Laddar försäljningsdata...</div>;
  }

  const { labels, orders, revenue } = chartData;

  const chartDataset = {
    labels,
    datasets: [
      {
        label: 'Antal beställningar',
        data: orders,
        borderColor: 'rgb(99, 102, 241)',
        backgroundColor: 'rgba(99, 102, 241, 0.1)',
        borderWidth: 3,
        fill: true,
        tension: 0.4,
        pointBackgroundColor: 'rgb(99, 102, 241)',
        pointBorderColor: 'white',
        pointBorderWidth: 2,
        pointRadius: 5,
        pointHoverRadius: 7,
        yAxisID: 'y'
      },
      {
        label: 'Omsättning (kr)',
        data: revenue,
        borderColor: 'rgb(16, 185, 129)',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        borderWidth: 2,
        fill: true,
        tension: 0.3,
        pointBackgroundColor: 'rgb(16, 185, 129)',
        pointBorderColor: 'white',
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        yAxisID: 'y1'
      }
    ]
  };

  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgb(99, 102, 241)',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          label: function(context: any) {
            if (context.datasetIndex === 0) {
              return `${context.parsed.y} beställningar`;
            }
            return `${context.parsed.y.toLocaleString('sv-SE')} kr omsättning`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
          borderColor: 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          color: 'rgba(0, 0, 0, 0.6)',
          font: {
            size: 12
          }
        }
      },
      y: {
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
          borderColor: 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          color: 'rgba(0, 0, 0, 0.6)',
          font: {
            size: 12
          },
          callback: function(value: any) {
            return `${value} beställningar`;
          }
        },
        beginAtZero: true
      },
      y1: {
        position: 'right' as const,
        grid: {
          drawOnChartArea: false,
          color: 'rgba(0, 0, 0, 0.05)',
          borderColor: 'rgba(0, 0, 0, 0.1)'
        },
        ticks: {
          color: 'rgba(16, 185, 129, 0.8)',
          font: {
            size: 12
          },
          callback: function(value: any) {
            return `${Number(value).toLocaleString('sv-SE')} kr`;
          }
        },
        beginAtZero: true
      }
    },
    elements: {
      point: {
        hoverBackgroundColor: 'rgb(99, 102, 241)',
      }
    }
  };

  return (
    <div style={{ height: '300px', width: '100%' }}>
      <Line data={chartDataset} options={options} />
    </div>
  );
};

export default SalesChart;
